/**
 * Calculator Registry
 *
 * Manages registration and retrieval of calculator objects.
 * Implements the Strategy pattern - calculators are registered by methodId
 * and can be retrieved to perform calculations.
 */

const CalculatorRegistry = (function () {
	function Registry() {
		this.methods = {};
		this.defaultMethodId = 'private_company_409a';
	}

	/**
	 * Register a calculator
	 * @param {Object} calculator - Calculator object implementing the interface
	 * @returns {Registry} - Returns this for chaining
	 */
	Registry.prototype.register = function (calculator) {
		// Validate calculator has required properties
		const required = ['methodId', 'displayName', 'calculate', 'requiredDataSources'];
		for (let i = 0; i < required.length; i++) {
			if (!(required[i] in calculator)) {
				throw new Error('Calculator missing required property: ' + required[i]);
			}
		}

		this.methods[calculator.methodId] = calculator;
		return this; // Allow chaining
	};

	/**
	 * Get a calculator by method ID
	 * @param {string} methodId - The calculator method ID
	 * @returns {Object} - The calculator object
	 */
	Registry.prototype.get = function (methodId) {
		let calculator = this.methods[methodId];
		if (!calculator) {
			console.warn('Unknown calculation method: ' + methodId + ', using default: ' + this.defaultMethodId);
			calculator = this.methods[this.defaultMethodId];
			if (!calculator) {
				throw new Error('Default calculator not registered: ' + this.defaultMethodId);
			}
		}
		return calculator;
	};

	/**
	 * Check if a method is registered
	 * @param {string} methodId - The calculator method ID
	 * @returns {boolean}
	 */
	Registry.prototype.has = function (methodId) {
		return methodId in this.methods;
	};

	/**
	 * List all available calculators
	 * @returns {Array} - Array of { methodId, displayName, requiredDataSources }
	 */
	Registry.prototype.listAvailable = function () {
		const result = [];
		for (const key in this.methods) {
			if (this.methods.hasOwnProperty(key)) {
				const m = this.methods[key];
				result.push({
					methodId: m.methodId,
					displayName: m.displayName,
					requiredDataSources: m.requiredDataSources,
				});
			}
		}
		return result;
	};

	/**
	 * Set the default calculator method
	 * @param {string} methodId - The default method ID
	 */
	Registry.prototype.setDefault = function (methodId) {
		if (!this.has(methodId)) {
			throw new Error('Cannot set default to unregistered method: ' + methodId);
		}
		this.defaultMethodId = methodId;
	};

	return Registry;
})();

export { CalculatorRegistry };
